import { useState } from "react";
import {
  createMediator,
  type EventNode,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyHandlerRef,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export type ToggleEvent = {
  type: "view-toggle.requested";
  selectionCollapsed?: boolean;
  value?: boolean;
};

interface TogglePresentation {
  node: EventNode<TypedEvent>;
  pressed: boolean;
  onEvent: (event: ToggleEvent) => void;
}

export function useTogglePresenter(
  initialPressed: boolean,
  canToggle: (pressed: boolean) => boolean = () => true,
): TogglePresentation {
  const eligibility = useHierarchyHandlerRef(canToggle);
  const [mediator] = useState(() =>
    createMediator<boolean, ToggleEvent>(initialPressed, (state, event) => ({
      status: "handled",
      state:
        event.selectionCollapsed === false || !eligibility.current(state)
          ? state
          : (event.value ?? !state),
    })),
  );
  useHierarchyLifetime(mediator);
  const pressed = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (event.type !== "view-toggle.requested") return "unhandled";
    return mediator.dispatch(event as ToggleEvent).status === "handled"
      ? "handled"
      : "unhandled";
  });
  return { node, pressed, onEvent: node.dispatch };
}
